import { useEffect, useMemo, useState } from 'react'

import curriculumSanity from '~/shared/sanity/curriculum'
import { getCurriculumRelevantCountryCodeAndGradeId } from '~/shared/sanity/utils/curriculum.utils'

// Module-level caches shared across renders so a curriculum is only fetched once per session. In-flight
// requests are cached too, so components mounting together (certificate list + open certificate) share one.
const labelsByCountryAndLevel = new Map<string, string>()
const requestsByCountryCode = new Map<string, Promise<Record<string, string>>>()

export interface GradeRef {
  countryCode: string
  gradeLevel: number
}

const toCurriculumCountryCode = (countryCode: string): string =>
  getCurriculumRelevantCountryCodeAndGradeId(countryCode).curriculumCountryCode

const fetchCountryLabels = (countryCode: string): Promise<Record<string, string>> => {
  const inFlight = requestsByCountryCode.get(countryCode)
  if (inFlight) return inFlight

  const request = curriculumSanity
    .getCurriculumGradeLabels(countryCode)
    .then((fetched) => {
      Object.entries(fetched).forEach(([key, label]) => labelsByCountryAndLevel.set(key, label))
      return fetched
    })
    // Drop the cached request on failure so a later render can retry.
    .catch(() => {
      requestsByCountryCode.delete(countryCode)
      return {}
    })

  requestsByCountryCode.set(countryCode, request)
  return request
}

/**
 * Resolves a grade's real curriculum label (Sanity `gradeLabel`) from its countryCode + gradeLevel —
 * the same name the select-grade page shows. gradeLevel alone is meaningless across curricula: level
 * 15 is "S1" in Norway and "Statistics and probability" in the US, so the `common:gradeLevels` i18n map
 * (one country-agnostic level -> ordinal map) cannot label a certificate correctly.
 *
 * Returns a lookup that yields '' until the labels arrive, so a wrong grade name is never rendered.
 */
const useCurriculumGradeLabels = (grades?: GradeRef[]): ((grade?: GradeRef | null) => string) => {
  const [labels, setLabels] = useState<Record<string, string>>(() => Object.fromEntries(labelsByCountryAndLevel))

  const countryCodes = useMemo(
    () => Array.from(new Set((grades ?? []).map((grade) => toCurriculumCountryCode(grade.countryCode)))).toSorted(),
    [grades],
  )
  const countryCodesKey = countryCodes.join('|')

  useEffect(() => {
    if (!countryCodes.length) return

    let isActive = true
    Promise.all(countryCodes.map(fetchCountryLabels)).then(() => {
      if (isActive) setLabels(Object.fromEntries(labelsByCountryAndLevel))
    })

    return () => {
      isActive = false
    }
    // countryCodesKey is a stable string form of countryCodes, so it captures every change to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCodesKey])

  return useMemo(
    () => (grade) => grade ? labels[`${toCurriculumCountryCode(grade.countryCode)}-${grade.gradeLevel}`] ?? '' : '',
    [labels],
  )
}

export default useCurriculumGradeLabels
